import { useMemo, useState } from "react";
import { deskCard, deskEyebrow, deskMuted } from "./salesDeskStyles";
import { filterSalesDeskOrders, type SalesDeskOrder } from "./salesDeskOrderFilter";

const searchClass =
  "mt-6 w-full rounded-lg border border-[#dce3dc] bg-white px-4 py-3 text-[#142d2d] outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

export function SalesDeskOrders({
  loaded,
  onOpen,
}: {
  loaded: { orders: SalesDeskOrder[]; loading: boolean; error: string | null };
  onOpen: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const orders = useMemo(() => filterSalesDeskOrders(loaded.orders, query), [loaded.orders, query]);

  return (
    <div>
      <p className={deskEyebrow}>Order lookup</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Find the sale. See the next handoff.</h1>
      <p className={`mt-3 max-w-[40rem] ${deskMuted}`}>Sale and fulfillment statuses stay separate.</p>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search orders"
        placeholder="Search customer, order or serial"
        className={searchClass}
      />
      {loaded.error ? <p className="mt-4 text-sm text-red-800 dark:text-red-200">{loaded.error}</p> : null}
      {loaded.loading && loaded.orders.length === 0 ? <p className={`mt-6 ${deskMuted}`}>Loading orders…</p> : null}
      {!loaded.loading && !loaded.error && loaded.orders.length === 0 ? (
        <p className={`mt-6 ${deskMuted}`}>No sales yet.</p>
      ) : null}
      {!loaded.loading && loaded.orders.length > 0 && orders.length === 0 ? (
        <p className={`mt-6 ${deskMuted}`}>No matching orders.</p>
      ) : null}
      {orders.length > 0 ? (
        <section className={`${deskCard} mt-6`}>
          {orders.map((order) => (
            <button
              key={order.id}
              type="button"
              onClick={() => onOpen(order.id)}
              className="flex w-full items-center gap-3 border-b border-[#dce3dc] py-4 text-left last:border-b-0 dark:border-zinc-700"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">
                  {order.customer}
                  <span className="ml-2 font-normal text-[#687772] dark:text-zinc-400">{order.id.slice(0, 8)}</span>
                </span>
                <span className="mt-1 block text-xs text-[#687772] dark:text-zinc-400">
                  {order.model.trim() || "Model not on this sale"}
                </span>
                <span className="mt-1 block text-xs text-[#687772] dark:text-zinc-400">{order.serial ?? "No serial yet"}</span>
                <span className="mt-2 flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#eef1e7] px-2.5 py-1 text-[11px] font-semibold text-[#164b43] dark:bg-zinc-800 dark:text-emerald-200">
                    {order.saleStatus}
                  </span>
                  <span className="rounded-full bg-[#e4edf2] px-2.5 py-1 text-[11px] font-semibold text-[#315d75] dark:bg-zinc-800 dark:text-sky-200">
                    {order.fulfillment}
                  </span>
                </span>
              </span>
              <span aria-hidden="true">→</span>
            </button>
          ))}
        </section>
      ) : null}
    </div>
  );
}
